import type { ApiStrategy, ParameterSchema } from "@tradingdroid/frontends-types";
import { isParamVisible } from "@src/components/ParamOverridesEditor/utils";

/**
 * Extract the default value from a parameter schema entry.
 */
const getDefaultValue = (param: ParameterSchema): unknown => {
  return param.default;
};

/**
 * Build defaults for a single config section, respecting parameter dependencies.
 *
 * Two-pass approach:
 * 1. Collect defaults for all params (needed as context for dependency checks)
 * 2. Filter out params whose dependencies aren't satisfied by those defaults
 *
 * Assumption: serialized dependency chains are at most 1 level deep (e.g. atr_length → calc_type).
 * The backend (strategy_sync.py) only serializes simple non-validation REQUIRES deps; parent-child
 * relationships between categorical params use conditionalCategories instead of dependencies[].
 * If a future strategy introduces a transitive chain (C → B → A) of serialized dependencies,
 * this two-pass approach would need to become iterative (prune until stable).
 */
const buildSectionDefaults = (schema: Record<string, ParameterSchema>): Record<string, unknown> => {
  // Pass 1: collect all defaults
  const allDefaults: Record<string, unknown> = {};
  for (const [name, param] of Object.entries(schema)) {
    const val = getDefaultValue(param);
    // A null default is a parameter declaring it has none, not a default worth zero. Kept here it
    // would reach a config textarea as a literal null, and mark the row as carrying a strategy
    // default, on a parameter the run leaves to the strategy. Loose check on purpose: 0 and false
    // are defaults a strategy can mean.
    if (val != null) allDefaults[name] = val;
  }

  // Pass 2: filter by dependency visibility
  const filtered: Record<string, unknown> = {};
  for (const [name, param] of Object.entries(schema)) {
    if (!(name in allDefaults)) continue;
    if (!isParamVisible(param.dependencies, allDefaults)) continue;
    filtered[name] = allDefaults[name];
  }

  return filtered;
};

/**
 * Build default strategy + exit config from a strategy's parameter schema.
 * Extracts default values for each parameter,
 * then filters out params whose dependencies aren't satisfied.
 */
export const buildDefaultConfig = (
  strategy: ApiStrategy,
): Record<string, Record<string, unknown>> => {
  const config: Record<string, Record<string, unknown>> = {};
  const schema = strategy?.parameters_schema;
  if (schema?.strategy_config) {
    config.strategy_config = buildSectionDefaults(schema.strategy_config);
  }
  if (schema?.exit_config) {
    config.exit_config = buildSectionDefaults(schema.exit_config);
  }
  return config;
};
